import { Frame, FrameStatus } from 'dragonmouse-shared/interfaces';
import { getFrameStatusConfig } from 'dragonmouse-shared/frame_status';
import { useEffect, useRef, useState } from 'react';
// Reset displayed progress only on isProcessing's false→true edge — i.e. when
// a frame newly enters PROCESSING. Preserves the value through the brief
// shouldAnimate gap at PROCESSING→image-loading (thumbnail mounts <img> late).

// Smoothing config — tune the catch-up feel here.
//
// The moon's *target* progress comes from the FRAME_STATUS_TABLE formula and
// can pop forward when a phase ends early (e.g. statusMessage transitions from
// "generating design" to "generating 1 image" while the formula was still at
// 0.05 — the new startCap is 0.8). Without smoothing the moon snaps. With this
// cap in place the moon advances at most this many progress-units per second,
// turning the snap into a smooth travel.
//
// Sized to be much faster than typical natural progression (~0.5–2%/sec inside
// a phase) so steady-state motion isn't slowed down — only the inter-phase
// jumps get smoothed.
const MAX_FORWARD_VELOCITY_PER_SEC = 0.12;
const TICK_INTERVAL_MS = 100;

const computeTarget = (frame: Frame, isImageLoading: boolean): number => {
	if (frame.metadata.status !== FrameStatus.PROCESSING) {
		return isImageLoading ? 1 : 0;
	}
	const statusConfig = getFrameStatusConfig(frame.metadata.statusMessage ?? '');
	const phaseStartMs = frame.metadata.statusUpdatedAt
		? new Date(frame.metadata.statusUpdatedAt).getTime()
		: frame.createdAt
			? new Date(frame.createdAt).getTime()
			: Date.now();
	const phaseElapsedSec = Math.max(0, (Date.now() - phaseStartMs) / 1000);
	const phaseProgress = Math.min(phaseElapsedSec / statusConfig.budgetSec, 1);
	return statusConfig.startCap + (statusConfig.cap - statusConfig.startCap) * phaseProgress;
};

// Whether we're stalled at the current phase's cap waiting for the next status —
// drives the moon's breathing animation.
const computeIsIndeterminate = (frame: Frame, target: number): boolean => {
	if (frame.metadata.status !== FrameStatus.PROCESSING) return false;
	const cap = getFrameStatusConfig(frame.metadata.statusMessage ?? '').cap;
	return target >= cap && cap < 1;
};

export interface FrameMoonProgress {
	progress: number;
	isIndeterminate: boolean;
}

// Single source of truth for moon-phase driving. FrameView, the frame inspector's Prototype and
// the chat Thumbnail all call this so the loading indicators stay in sync. The hook keeps a
// rate-limited *displayed* progress state and ticks every 100ms while the moon
// is visible (PROCESSING or image-loading tail).
export const useFrameMoonProgress = (
	frame: Frame | null | undefined,
	isImageLoading: boolean
): FrameMoonProgress => {
	const [displayedProgress, setDisplayedProgress] = useState(0);
	const [isIndeterminate, setIsIndeterminate] = useState(false);
	const lastTickRef = useRef<number>(Date.now());
	const frameRef = useRef(frame);
	const isImageLoadingRef = useRef(isImageLoading);
	frameRef.current = frame;
	isImageLoadingRef.current = isImageLoading;

	const isProcessing = frame?.metadata.status === FrameStatus.PROCESSING;
	const shouldAnimate = !!frame && (isProcessing || isImageLoading);

	const wasProcessingRef = useRef(isProcessing);
	useEffect(() => {
		if (!wasProcessingRef.current && isProcessing) {
			setDisplayedProgress(0);
		}
		wasProcessingRef.current = isProcessing;
	}, [isProcessing]);

	useEffect(() => {
		if (!shouldAnimate) {
			setIsIndeterminate(false);
			return;
		}
		// Preserve displayedProgress across shouldAnimate flips. The thumbnail
		// briefly toggles shouldAnimate off when the frame leaves PROCESSING but
		// before its <img> mounts and flips isImageLoading=true; resetting here
		// would pop the moon back to 0 and re-climb. Initial mount starts at 0
		// via useState, which covers the fresh-frame case.
		lastTickRef.current = Date.now();
		const interval = setInterval(() => {
			const f = frameRef.current;
			if (!f) return;
			const now = Date.now();
			const dt = (now - lastTickRef.current) / 1000;
			lastTickRef.current = now;
			const target = computeTarget(f, isImageLoadingRef.current);
			setDisplayedProgress((prev) =>
				target > prev ? Math.min(prev + MAX_FORWARD_VELOCITY_PER_SEC * dt, target) : prev
			);
			setIsIndeterminate(computeIsIndeterminate(f, target));
		}, TICK_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [shouldAnimate]);

	return { progress: displayedProgress, isIndeterminate };
};
